import React, { useEffect, useRef, useState } from 'react';
import { Loader2, PackageX, Plus, Lock, PackageCheck, AlertTriangle } from 'lucide-react';
import { buildHorsStockSaleLine } from './posV2HorsStockUtils';
import { fmt } from './posV2Utils';

/**
 * Normalisation pour la comparaison désignation / référence :
 * accents retirés (NFD + marques combinantes), minuscules, trim, espaces compactés.
 */
export const normalizeLabel = (value) => String(value || '')
  .normalize('NFD')
  // plage U+0300–U+036F (marques diacritiques combinantes) — caractères littéraux
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim()
  .replace(/\s+/g, ' ');

/**
 * Cherche dans la liste stock un article dont la désignation (ou la référence)
 * correspond exactement au libellé saisi, accents/casse/espaces ignorés.
 * Priorité à un article avec stock > 0 dans le magasin de session.
 */
export const findExactStockMatch = (items, label, sessionMag) => {
  const wanted = normalizeLabel(label);
  if (!wanted) return null;
  const mag = String(sessionMag || '').toUpperCase();
  const candidates = (Array.isArray(items) ? items : []).filter((a) => {
    if (!a || a.is_archived || a.is_active === false) return false;
    return normalizeLabel(a.designation) === wanted || normalizeLabel(a.reference) === wanted;
  });
  if (!candidates.length) return null;
  const sameMag = candidates.filter((a) => !mag || !a.magasin_id || String(a.magasin_id).toUpperCase() === mag);
  const pool = sameMag.length ? sameMag : candidates;
  return pool.find((a) => Number(a.quantite) > 0) || pool[0];
};

/**
 * Hors stock = créer une ligne produit ad hoc pour encaisser une vente
 * (même logique que /pos legacy « ajout manuel » · ref HORS-STOCK).
 *
 * Si la désignation matche un article catalogue :
 *  - prix catalogue verrouillé (sauf Carlos) ;
 *  - stock > 0 → ligne catalogue NORMALE (produit_ref = référence article,
 *    stock_magasin_id = magasin de session) → le stock est décrémenté ;
 *  - stock = 0 → ligne hors-stock « Rupture » au prix verrouillé
 *    (produit_ref = référence article, stock_magasin_id null).
 *  Dans les deux cas on n'enregistre PAS un doublon dans le catalogue.
 */
const PosV2HorsStockDrawer = ({ api, sessionMag, carlosUser = false, onAddLine, onClose }) => {
  const [designation, setDesignation] = useState('');
  const [prix, setPrix] = useState('');
  const [quantite, setQuantite] = useState('1');
  const [registerCatalog, setRegisterCatalog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [looking, setLooking] = useState(false);
  const [priceLocked, setPriceLocked] = useState(false);
  const [matched, setMatched] = useState(null);
  const [error, setError] = useState('');
  const lookupSeq = useRef(0);

  useEffect(() => {
    const label = designation.trim();
    if (label.length < 2) {
      setPriceLocked(false);
      setMatched(null);
      return undefined;
    }
    const seq = ++lookupSeq.current;
    const t = setTimeout(async () => {
      setLooking(true);
      try {
        const res = await api.get('/stock', { params: { q: label, magasin_id: sessionMag } });
        if (seq !== lookupSeq.current) return;
        const exact = findExactStockMatch(Array.isArray(res.data) ? res.data : [], label, sessionMag);
        if (exact && Number(exact.prix_vente) > 0) {
          setPrix(String(Number(exact.prix_vente)));
          setMatched({
            id: exact.id,
            reference: exact.reference,
            designation: exact.designation || label,
            prix_vente: Number(exact.prix_vente),
            quantite: Number(exact.quantite) || 0,
            magasin_id: exact.magasin_id || sessionMag,
            thumbnail_url: exact.image_url || null,
          });
          setPriceLocked(!carlosUser);
        } else {
          setPriceLocked(false);
          setMatched(null);
        }
      } catch {
        if (seq === lookupSeq.current) {
          setPriceLocked(false);
          setMatched(null);
        }
      } finally {
        if (seq === lookupSeq.current) setLooking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [designation, api, sessionMag, carlosUser]);

  const matchedInStock = Boolean(matched) && matched.quantite > 0;
  const qtyNum = Math.max(1, parseInt(quantite, 10) || 1);
  const qtyOverStock = matchedInStock && qtyNum > matched.quantite;

  const submit = async () => {
    setError('');
    const unit = parseFloat(prix);
    if (matched) {
      // Prix verrouillé (vendeur) : on force la valeur catalogue, quoi qu'affiche l'input.
      const finalUnit = priceLocked ? matched.prix_vente : unit;
      if (!finalUnit || finalUnit <= 0) {
        setError('Prix TTC requis');
        return;
      }
      setLoading(true);
      try {
        if (matchedInStock) {
          // Ligne catalogue normale → décrément stock côté serveur.
          onAddLine({
            product_id: matched.id,
            produit_ref: matched.reference,
            designation: matched.designation,
            quantite: qtyNum,
            prix_unitaire: finalUnit,
            prix_total: finalUnit * qtyNum,
            prix_vente_original: matched.prix_vente,
            stock_magasin_id: String(sessionMag || matched.magasin_id || '').toUpperCase(),
            thumbnail_url: matched.thumbnail_url,
            is_hors_stock: false,
          });
        } else {
          // Rupture : hors-stock au prix verrouillé, référence catalogue conservée.
          onAddLine({
            product_id: matched.id,
            produit_ref: matched.reference,
            designation: matched.designation,
            quantite: qtyNum,
            prix_unitaire: finalUnit,
            prix_total: finalUnit * qtyNum,
            prix_vente_original: matched.prix_vente,
            stock_magasin_id: null,
            thumbnail_url: matched.thumbnail_url,
            is_hors_stock: true,
            hors_stock_rupture: true,
          });
        }
        onClose();
      } finally {
        setLoading(false);
      }
      return;
    }

    const line = buildHorsStockSaleLine({ designation, prix, quantite });
    if (!line) {
      setError('Désignation et prix TTC requis');
      return;
    }
    setLoading(true);
    try {
      if (registerCatalog) {
        await api.post('/stock/manual-add', {
          designation: designation.trim(),
          prix_vente: parseFloat(prix),
        });
      }
      onAddLine(line);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400 flex items-start gap-2">
        <PackageX size={18} className="text-orange-400 shrink-0 mt-0.5" />
        <span>
          Vendre un article <strong className="text-white">hors catalogue / hors stock</strong>
          {' '}— la ligne part en caisse avec le badge HORS STOCK (pas de décrément stock).
          {!carlosUser && ' La vente sera enregistrée dans l’historique après validation par la direction.'}
          {' '}Si la désignation correspond à un article du catalogue, il est rattaché automatiquement.
        </span>
      </p>

      {error && (
        <p className="text-xs text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
      )}

      <label className="block text-xs text-gray-400">
        Désignation *
        <input
          className="mt-1 w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 min-h-[52px] text-white"
          placeholder="Ex. Coque iPhone 15 Pro — commande spéciale"
          value={designation}
          onChange={(e) => setDesignation(e.target.value)}
          data-testid="pos-v2-hors-stock-designation"
        />
      </label>

      {looking && !matched && designation.trim().length >= 2 && (
        <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <Loader2 size={12} className="animate-spin" /> Recherche dans le catalogue…
        </p>
      )}

      {matched && (
        <div
          className={`rounded-lg border px-3 py-2.5 space-y-1 ${
            matchedInStock
              ? 'border-emerald-500/30 bg-emerald-950/20'
              : 'border-amber-500/30 bg-amber-950/20'
          }`}
          data-testid="pos-v2-hors-stock-match"
        >
          <p className={`text-xs font-semibold flex items-center gap-1.5 ${matchedInStock ? 'text-emerald-200' : 'text-amber-200'}`}>
            {matchedInStock ? <PackageCheck size={13} /> : <Lock size={13} />}
            Article catalogue trouvé{looking ? ' …' : ''}
          </p>
          <p className="text-sm text-white truncate">{matched.designation}</p>
          <p className="text-[11px] text-gray-300 flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="font-mono">Réf. {matched.reference}</span>
            <span>Prix catalogue {fmt(matched.prix_vente)} €</span>
            <span className={matchedInStock ? 'text-emerald-300' : 'text-amber-300'}>
              Stock {matched.magasin_id || sessionMag} : {matched.quantite}
            </span>
          </p>
          <p className="text-[11px] text-gray-400">
            {matchedInStock
              ? 'Sera ajouté comme ligne catalogue normale — le stock sera décrémenté.'
              : 'Rupture de stock — ligne HORS STOCK au prix catalogue, référence conservée.'}
            {priceLocked ? ' Prix verrouillé.' : ' Prix modifiable (accès direction).'}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs text-gray-400">
          Prix TTC (€) *
          <input
            type="number"
            min="0"
            step="0.01"
            disabled={priceLocked}
            className="mt-1 w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 min-h-[52px] text-white disabled:opacity-60"
            placeholder="29.90"
            value={prix}
            onChange={(e) => setPrix(e.target.value)}
            data-testid="pos-v2-hors-stock-prix"
          />
        </label>
        <label className="block text-xs text-gray-400">
          Quantité
          <input
            type="number"
            min="1"
            step="1"
            className="mt-1 w-full px-3 py-3 rounded-lg bg-black/30 border border-white/10 min-h-[52px] text-white"
            value={quantite}
            onChange={(e) => setQuantite(e.target.value)}
          />
        </label>
      </div>

      {qtyOverStock && (
        <p className="text-[11px] text-amber-300 flex items-center gap-1.5">
          <AlertTriangle size={12} />
          Quantité supérieure au stock disponible ({matched.quantite}).
        </p>
      )}

      {!matched && (
        <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={registerCatalog}
            onChange={(e) => setRegisterCatalog(e.target.checked)}
          />
          <span>Enregistrer aussi dans le catalogue (qty 0)</span>
        </label>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={loading}
        className="w-full min-h-[52px] rounded-xl bg-red-600 hover:bg-red-500 disabled:opacity-40 font-bold text-white flex items-center justify-center gap-2"
        data-testid="pos-v2-hors-stock-add"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
        {matchedInStock ? 'Ajouter au panier (article catalogue)' : 'Ajouter au panier'}
      </button>
    </div>
  );
};

export default PosV2HorsStockDrawer;
